import { useGraphCacheStore } from '@tunasong/graph-cache'
import { profilesApi, useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted, Profile } from '@tunasong/schemas'
import { useShallow } from 'zustand/react/shallow'

type ISO8601 = string

const HALF_LIFE_DAYS = 30

const SYSTEM_PRINCIPALS = new Set(['PUBLIC', 'AUTHENTICATED'])

export interface PastCollaboratorEntry {
  profile: Persisted<Profile>
  /** Distinct entities owned by the current user that have this principal in their ACL. */
  count: number
  /** max(entity.updatedAt) across those entities. */
  lastSharedAt: ISO8601
  /** count × recencyWeight(lastSharedAt, now). See `collaboratorScore`. */
  score: number
}

export interface UsePastCollaboratorsOptions {
  /** Principals to omit (the current user, system principals, current-entity ACLs). */
  excludePrincipals?: string[]
  /** Cap on returned entries after ranking. Default 20. */
  limit?: number
}

export interface RawPastCollaborator {
  principal: string
  count: number
  lastSharedAt: ISO8601
  score: number
}

export const collaboratorScore = (count: number, lastSharedAt: ISO8601, now: number = Date.now()): number => {
  const ageMs = now - new Date(lastSharedAt).getTime()
  const ageDays = Math.max(0, ageMs / 86_400_000)
  const recencyWeight = Math.pow(0.5, ageDays / HALF_LIFE_DAYS)
  return count * recencyWeight
}

interface AggregateOptions {
  excludePrincipals?: string[]
  limit?: number
  now?: number
}

interface PrincipalTally {
  principal: string
  count: number
  lastSharedAt: ISO8601
}

/** Per principal, the entities the current user owns that share with it. Independent of the clock. */
const tallyPrincipals = (
  entities: Iterable<Persisted<Entity>>,
  currentUserId: string,
  excludePrincipals: string[] = []
): PrincipalTally[] => {
  const excluded = new Set([currentUserId, ...excludePrincipals])

  const byPrincipal = new Map<string, { count: number; lastSharedAt: ISO8601 }>()

  for (const entity of entities) {
    if (entity.userId !== currentUserId) {
      continue
    }
    const acls = entity.acls ?? []
    for (const acl of acls) {
      const principal = acl.principal
      if (SYSTEM_PRINCIPALS.has(principal)) {
        continue
      }
      if (excluded.has(principal)) {
        continue
      }
      const prev = byPrincipal.get(principal)
      if (!prev) {
        byPrincipal.set(principal, { count: 1, lastSharedAt: entity.updatedAt })
      } else {
        prev.count += 1
        if (entity.updatedAt > prev.lastSharedAt) {
          prev.lastSharedAt = entity.updatedAt
        }
      }
    }
  }

  return Array.from(byPrincipal, ([principal, { count, lastSharedAt }]) => ({ principal, count, lastSharedAt }))
}

const rankTallies = (tallies: PrincipalTally[], limit: number, now: number): RawPastCollaborator[] => {
  const ranked = tallies.map(tally => ({ ...tally, score: collaboratorScore(tally.count, tally.lastSharedAt, now) }))

  ranked.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score
    }
    if (b.lastSharedAt !== a.lastSharedAt) {
      return b.lastSharedAt < a.lastSharedAt ? -1 : 1
    }
    return a.principal < b.principal ? -1 : a.principal > b.principal ? 1 : 0
  })

  return ranked.slice(0, limit)
}

/**
 * Pure aggregation: walks the given entities, collects principals from ACLs of entities
 * the current user owns, ranks them by `count × recencyWeight(lastSharedAt)`.
 *
 * Exported separately from the hook so it can be unit-tested without React.
 */
export const aggregatePastCollaborators = (
  entities: Iterable<Persisted<Entity>>,
  currentUserId: string,
  options: AggregateOptions = {}
): RawPastCollaborator[] => {
  const { excludePrincipals = [], limit = 20, now = Date.now() } = options
  return rankTallies(tallyPrincipals(entities, currentUserId, excludePrincipals), limit, now)
}

/** A tally as one string, so `useShallow` compares tallies by value. Principals are ids and hold no newline. */
const encodeTally = ({ principal, count, lastSharedAt }: PrincipalTally) => [principal, count, lastSharedAt].join('\n')

const decodeTally = (encoded: string): PrincipalTally => {
  const [principal = '', count = '0', lastSharedAt = ''] = encoded.split('\n')
  return { principal, count: Number(count), lastSharedAt }
}

/**
 * React hook wrapping `aggregatePastCollaborators` with profile batching.
 *
 * Walks the session-wide entity cache, ranks principals from ACLs of entities the
 * current user owns, batch-loads profiles, and drops principals whose profile didn't
 * resolve (deleted users, etc.).
 *
 * It only sees what is cached right now. The cache evicts entries nobody has referenced for a while (GH #1643),
 * so a collaborator shared with only through long-unvisited entities drops out until those are loaded again.
 * The selector re-renders only when a tally changes, not on every cache write.
 *
 * Returns `[]` on first render and whenever the cache yields no eligible principals.
 */
export const usePastCollaborators = (options: UsePastCollaboratorsOptions = {}): PastCollaboratorEntry[] => {
  const { userId } = useCurrentUser()
  const { excludePrincipals, limit = 20 } = options
  const tallies = useGraphCacheStore(
    useShallow(state =>
      userId ? tallyPrincipals(state.entityCache.values(), userId, excludePrincipals).map(encodeTally) : []
    )
  )

  const ranked = rankTallies(tallies.map(decodeTally), limit, Date.now())
  const principalIds = ranked.map(r => r.principal)

  const { currentData: profiles = [] } = profilesApi.useLoadProfilesQuery(
    { userIds: principalIds },
    { skip: principalIds.length === 0 }
  )

  if (!userId) {
    return []
  }

  const profileByUserId = new Map(profiles.map(p => [p.userId, p]))

  const entries: PastCollaboratorEntry[] = []
  for (const raw of ranked) {
    const profile = profileByUserId.get(raw.principal)
    if (!profile) {
      continue
    }
    entries.push({
      profile,
      count: raw.count,
      lastSharedAt: raw.lastSharedAt,
      score: raw.score,
    })
  }
  return entries
}
